// src/pages/DeliveryPage.jsx
import { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import SeoMeta from '../components/SeoMeta';
import { usePageMeta } from '../hooks/usePageMeta';

const float = keyframes`
  0%   { transform: translateY(0) }
  50%  { transform: translateY(-6px) }
  100% { transform: translateY(0) }
`;

const Wrap = styled.div``;

/* ---------- HERO ---------- */
const Hero = styled.section`
  position: relative;
  padding: clamp(60px, 8vw, 120px) 0 64px;
  overflow: hidden;

  .bg {
    position: absolute; inset: -25%;
    background:
      radial-gradient(40% 40% at 20% 0%, #ff660022, transparent 60%),
      radial-gradient(38% 38% at 85% 15%, #00c2ff22, transparent 60%),
      radial-gradient(50% 50% at 50% 120%, #a8ff0030, transparent 60%);
    filter: blur(48px);
    transform: translateZ(0);
  }

  .inner {
    position: relative;
    width: ${({theme})=>theme.container}; margin: 0 auto;
    display: grid; grid-template-columns: 1.1fr .9fr; gap: 36px;
  }
  @media (max-width: 980px){ .inner{ grid-template-columns: 1fr; } }

  .lead{ font-size: clamp(18px, 2.2vw, 20px); color: ${({theme})=>theme.textMuted}; margin-bottom: 18px; }
  .badges{ display:flex; gap:10px; flex-wrap: wrap; margin: 10px 0 16px; }
  .badge{
    padding: 6px 10px; font-size: 14px;
    border:1px solid ${({theme})=>theme.border}; background: ${({theme})=>theme.surface};
    border-radius: ${({theme})=>theme.radius.pill}px;
  }
  .cta{ display:flex; gap: 10px; flex-wrap: wrap; }
`;

const Media = styled.div`
  border:1px solid ${({theme})=>theme.border};
  border-radius: ${({theme})=>theme.radius.xl}px;
  overflow:hidden; position: relative;
  min-height: 280px; background: ${({theme})=>theme.body};
  img{ width:100%; height:100%; object-fit: cover; display:block; transform: scale(1.03); }
`;

/* ---------- GENERIC SECTION ---------- */
const Section = styled.section`
  padding: 64px 0;
  &.muted{ background: ${({theme})=>theme.body}; }
  .inner{ width: ${({theme})=>theme.container}; margin: 0 auto; }
  .head{ display:flex; align-items:end; justify-content:space-between; gap:16px; margin-bottom: 22px; }
  .kicker{ font-size: 13px; opacity:.7; letter-spacing:.12em; text-transform:uppercase; }
`;

/* ---------- GRID CARDS ---------- */
const Grid = styled.div`
  display:grid; gap: 16px; grid-template-columns: repeat(3, minmax(0,1fr));
  @media (max-width: 980px){ grid-template-columns: 1fr; }
`;

const Card = styled.article`
  border:1px solid ${({theme})=>theme.border};
  background: ${({theme})=>theme.surface};
  border-radius: ${({theme})=>theme.radius.lg}px;
  padding: 16px; display:grid; gap: 8px;
  box-shadow: ${({theme})=>theme.shadow.sm};
  .icon{
    width:44px; height:44px; border-radius:12px; display:grid; place-items:center;
    background:${({theme})=>theme.body}; border:1px solid ${({theme})=>theme.border};
    animation:${float} 4s ease-in-out infinite;
  }
  h3{ margin: 2px 0 4px; }
  p{ margin:0; color:${({theme})=>theme.textMuted}; }
`;

/* ---------- TIMELINE ---------- */
const Timeline = styled.ol`
  display:grid; gap: 14px; counter-reset: s; padding:0; margin:0;
  li{
    position: relative; counter-increment: s;
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.surface};
    border-radius:${({theme})=>theme.radius.lg}px; padding:14px 16px; box-shadow:${({theme})=>theme.shadow.sm};
  }
  li b::before{
    content: counter(s) '. ';
    color: ${({theme})=>theme.accent}; font-weight: 900;
  }
  li p{ margin: 6px 0 0; color:${({theme})=>theme.textMuted}; }
`;

/* ---------- TABLE (zones) ---------- */
const TableWrap = styled.div`
  border:1px solid ${({theme})=>theme.border}; border-radius:${({theme})=>theme.radius.lg}px;
  overflow: hidden; background:${({theme})=>theme.surface}; box-shadow:${({theme})=>theme.shadow.sm};
  table{ width: 100%; border-collapse: collapse; }
  th, td{ padding: 12px 14px; border-bottom: 1px solid ${({theme})=>theme.border}; }
  th{ text-align: left; font-weight: 800; background:${({theme})=>theme.body}; }
  tr:last-child td{ border-bottom: none; }
`;

/* ---------- CTA ---------- */
const CTA = styled.section`
  padding: 72px 0 96px;
  .panel{
    width: ${({theme})=>theme.container}; margin: 0 auto;
    border:1px solid ${({theme})=>theme.border}; border-radius:${({theme})=>theme.radius.xl}px;
    background: linear-gradient(180deg, rgba(255,102,0,.08), transparent);
    padding: 28px; display:grid; gap: 12px; justify-items:center; text-align:center;
  }
  .row{ display:flex; gap:10px; flex-wrap:wrap; justify-content:center; }
`;

/* ---------- ICONS ---------- */
const Icon = ({ name }) => {
  const paths = {
    truck: <path d="M3 6h11v7h4l3 3v2h-2a2 2 0 11-4 0H9a2 2 0 11-4 0H3V6zm14 7V9h3l2 4h-5zM7 20a2 2 0 100-4 2 2 0 000 4zm10 0a2 2 0 100-4 2 2 0 000 4z" />,
    box:   <path d="M3 7l9-4 9 4-9 4-9-4zm0 3l9 4 9-4v7l-9 4-9-4V10z" />,
    doc:   <path d="M7 2h7l5 5v13a2 2 0 01-2 2H7a2 2 0 01-2-2V4a2 2 0 012-2zm7 1v5h5" />,
    clock: <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm1 5h-2v6l5 3 1-1-4-2V7z" />,
    pin:   <path d="M12 2a7 7 0 00-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 00-7-7zm0 9a2 2 0 110-4 2 2 0 010 4z" />,
    shield:<path d="M12 2l8 4v6c0 5-4 8-8 10-4-2-8-5-8-10V6l8-4z" />,
  };
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      {paths[name] || paths.truck}
    </svg>
  );
};

/* ---------- DATA ---------- */
const METHODS = [
  {
    k: 'truck',
    title: 'Власний транспорт',
    text: 'Крупногабаритні вироби та партії. Фіксація вантажу, тенти, страхування за запитом.',
  },
  {
    k: 'box',
    title: 'Нова Пошта / курʼєр',
    text: 'Невеликі вироби та комплектуючі по всій Україні. Відправка щоденно.',
  },
  {
    k: 'doc',
    title: 'Повний пакет документів',
    text: 'Рахунок, видаткова, техпаспорт/сертифікати, пакувальні листи. Електронний документообіг.',
  },
];

const TIMELINE = [
  { title: 'Запит', text: 'Уточнюємо габарити, вагу, адресу та обмеження за під’їздом.' },
  { title: 'План', text: 'Обираємо спосіб, дату й часовий слот, погоджуємо відповідальних осіб.' },
  { title: 'Пакування', text: 'Палети/жорстка тара, консервація, маркування місць, фото-звіт.' },
  { title: 'Доставка', text: 'Власний транспорт або перевізник. Трекінг і зв’язок з водієм.' },
  { title: 'Передача', text: 'Приймання за актом/накладною, рекомендації з монтажу/зберігання.' },
];

const ZONES = [
  { zone: 'Київ і область', eta: 'в той же/наступний день', price: 'від 600 ₴', note: 'Власний транспорт/курʼєр' },
  { zone: 'Центр (Черкаси, Кропивницький)', eta: '1–2 дні', price: 'від 900 ₴', note: 'Власний транспорт/перевізник' },
  { zone: 'Схід/Південь', eta: '1–3 дні', price: 'від 1200 ₴', note: 'Перевізник, страхування за запитом' },
  { zone: 'Захід', eta: '1–3 дні', price: 'від 1200 ₴', note: 'Перевізник' },
];

/* ---------- PAGE ---------- */
export default function DeliveryPage() {
  const meta = usePageMeta('delivery');
  useEffect(() => {
    AOS.init({ once: true, duration: 700, offset: 70, easing: 'ease-out-cubic' });
  }, []);

  return (
    <Wrap>
      <SeoMeta
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        image={meta.image}
        path={meta.path || '/delivery'}
      />
      {/* HERO */}
      <Hero>
        <div className="bg" aria-hidden />
        <div className="inner">
          <div data-aos="fade-up">
            <h1>Доставка та логістика</h1>
            <p className="lead">
              Доставляємо вироби по всій Україні: від невеликих комплектуючих до великогабаритних
              вузлів і металоконструкцій. Обираємо оптимальний спосіб, пакуємо, страхуємо — вчасно
              та безпечно.
            </p>
            <div className="badges" aria-hidden>
              <span className="badge">Власний транспорт</span>
              <span className="badge">Щоденні відправки</span>
              <span className="badge">Страхування вантажу</span>
            </div>
            <div className="cta">
              <Link className="btn btn--primary" to="/contacts">Розрахувати доставку</Link>
              <a className="btn btn--ghost" href="#methods">Способи доставки</a>
            </div>
          </div>

          <Media data-aos="zoom-in" data-aos-delay="100" role="img" aria-label="Вантажівка та пакування">
            <img src="/img/delivery/hero.jpg" alt="" />
          </Media>
        </div>
      </Hero>

      {/* METHODS */}
      <Section id="methods" className="muted">
        <div className="inner">
          <div className="head">
            <div>
              <div className="kicker">Опції</div>
              <h2>Як можемо доставити</h2>
            </div>
            <Link to="/contacts" className="btn btn--ghost">Поставити запитання</Link>
          </div>

          <Grid>
            {METHODS.map((m, i)=>(
              <Card key={m.k} data-aos="fade-up" data-aos-delay={i*70}>
                <div className="icon"><Icon name={m.k} /></div>
                <h3>{m.title}</h3>
                <p>{m.text}</p>
              </Card>
            ))}
          </Grid>
        </div>
      </Section>

      {/* TIMELINE */}
      <Section>
        <div className="inner">
          <div className="head">
            <div>
              <div className="kicker">Процес</div>
              <h2>Як це відбувається</h2>
            </div>
          </div>

          <Timeline>
            {TIMELINE.map((s, i)=>(
              <li key={i} data-aos="fade-up" data-aos-delay={i*60}>
                <b>{s.title}</b>
                <p>{s.text}</p>
              </li>
            ))}
          </Timeline>
        </div>
      </Section>

      {/* ZONES & PRICING (демо-орієнтири) */}
      <Section className="muted">
        <div className="inner">
          <div className="head">
            <div>
              <div className="kicker">Терміни та орієнтири</div>
              <h2>Зони доставки</h2>
            </div>
            <small style={{opacity:.7}}>Ціни орієнтовні — уточнюємо під час прорахунку</small>
          </div>

          <TableWrap data-aos="fade-up">
            <table>
              <thead>
                <tr>
                  <th>Зона</th>
                  <th>Термін</th>
                  <th>Вартість</th>
                  <th>Примітка</th>
                </tr>
              </thead>
              <tbody>
                {ZONES.map((z,i)=>(
                  <tr key={i}>
                    <td>{z.zone}</td>
                    <td>{z.eta}</td>
                    <td>{z.price}</td>
                    <td>{z.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </div>
      </Section>

      {/* SAFETY / PACKING */}
      <Section>
        <div className="inner">
          <div className="head">
            <div>
              <div className="kicker">Пакування</div>
              <h2>Захист виробів у дорозі</h2>
            </div>
          </div>

          <Grid>
            <Card data-aos="fade-up">
              <div className="icon"><Icon name="shield" /></div>
              <h3>Консервація та захист</h3>
              <p>Мастила/антикор, стретч, термоусадка, м’які прокладки — за специфікацією виробу.</p>
            </Card>
            <Card data-aos="fade-up" data-aos-delay="70">
              <div className="icon"><Icon name="box" /></div>
              <h3>Палети та жорстка тара</h3>
              <p>Палети з фіксацією, деревʼяні/металеві ящики, кріплення елементів, бирки та маркування.</p>
            </Card>
            <Card data-aos="fade-up" data-aos-delay="140">
              <div className="icon"><Icon name="clock" /></div>
              <h3>Фото-звіт</h3>
              <p>Фіксуємо стан пакування та вантажу перед відправкою, передаємо фото клієнту.</p>
            </Card>
          </Grid>
        </div>
      </Section>

      {/* CTA */}
      <CTA>
        <div className="panel" data-aos="fade-up">
          <h2 style={{margin:0}}>Потрібна доставка?</h2>
          <p style={{margin:'2px 0 10px', color:'var(--muted)'}}>
            Надішліть адресу, габарити й вагу — підготуємо план та кошторис.
          </p>
          <div className="row">
            <Link className="btn btn--primary" to="/contacts">Запросити розрахунок</Link>
            <a className="btn btn--ghost" href="mailto:info@momv.com.ua">info@momv.com.ua</a>
          </div>
        </div>
      </CTA>
    </Wrap>
  );
}
